import React from "react";
import { Button } from "../ui/button";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { SignaturePad } from "../SignaturePad";
import { euro, fmtDate } from "../../lib/format";
import { toast } from "sonner";
import { withPrep } from "../../lib/issuer";

export default function AcceptancePanel({ accept, agreed, agreedEarly, agreedVexatious, canAccept, cur, earlyActive, earlyVariants, email, firstName, hasProposal, hasVexatious, inRevision, issuerName, lastName, locked, quote, reject, resetQuantities, role, sendRevision, setAgreed, setAgreedEarly, setAgreedVexatious, setEmail, setFirstName, setHasSignature, setLastName, setRejectOpen, setRole, signatureRef, submitting }) {
  return (
    <div className="border-t border-border p-6 bg-slate-50/60">
      {inRevision ? (
        <div data-testid="accept-blocked-revision" className="flex items-start gap-2 text-amber-800 font-semibold text-sm">
          <Clock style={{ width: 18, height: 18 }} className="shrink-0" /> <span className="break-words min-w-0">Richiesta inviata, attendi l'aggiornamento {withPrep("di", issuerName)} prima di firmare.</span>
        </div>
      ) : hasProposal ? (
        <div data-testid="accept-hidden-proposal" className="text-sm">
          <p className="font-semibold text-amber-800">Hai modificato le quantità: questa è una proposta di modifica.</p>
          <p className="text-amber-700 mt-1">La firma non è disponibile finché {issuerName} non approva le nuove quantità. Invia la proposta, oppure ripristina le quantità originali per firmare il preventivo standard.</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button data-testid="accept-area-send-proposal" onClick={async () => {
              try { await sendRevision({ message: "", name: `${firstName} ${lastName}`.trim() }); toast.success(`Proposta inviata ${withPrep("a", issuerName)}!`); }
              catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
            }} className="font-semibold whitespace-normal h-auto min-h-9 py-2">
              Richiedi aggiornamento
            </Button>
            <Button variant="outline" data-testid="accept-area-reset" onClick={resetQuantities}>Ripristina quantità originali</Button>
          </div>
        </div>
      ) : locked ? (
        quote.status === "accettato" ? (
          <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
            <CheckCircle2 style={{ width: 18, height: 18 }} /> Accettato da {quote.accepted_name}
          </div>
        ) : quote.status === "scaduto" ? (
          <div className="flex items-center gap-2 text-slate-500 font-semibold text-sm">
            <Clock style={{ width: 18, height: 18 }} /> Preventivo scaduto — firma non disponibile
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
            <XCircle style={{ width: 18, height: 18 }} /> Preventivo rifiutato
          </div>
        )
      ) : (
        <>
          <h3 className="font-semibold text-slate-900 mb-3">Accetta e firma il preventivo</h3>
          <div className="grid grid-cols-2 gap-2.5 mb-2.5">
            <Input
              data-testid="public-accept-firstname-input"
              placeholder="Nome *"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              data-testid="public-accept-lastname-input"
              placeholder="Cognome *"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <Input
            data-testid="public-accept-email-input"
            type="email"
            placeholder="Email *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-2.5"
          />
          <Input
            data-testid="public-accept-role-input"
            placeholder="Ruolo / Qualifica (facoltativo)"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mb-4"
          />

          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Firma</p>
          <SignaturePad
            ref={signatureRef}
            fullName={`${firstName.trim()} ${lastName.trim()}`.trim()}
            onDirtyChange={setHasSignature}
          />

          <label className="flex items-start gap-2.5 mt-4 mb-2 cursor-pointer">
            <Checkbox data-testid="public-accept-checkbox" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
            <span className="text-sm text-slate-600 leading-snug">Accetto il preventivo per la configurazione selezionata e i Termini e Condizioni generali di fornitura.</span>
          </label>
          {hasVexatious && (
            <label className="flex items-start gap-2.5 mb-4 cursor-pointer">
              <Checkbox data-testid="public-accept-vexatious-checkbox" checked={agreedVexatious} onCheckedChange={setAgreedVexatious} className="mt-0.5" />
              <span className="text-sm text-slate-600 leading-snug">Ai sensi e per gli effetti degli artt. 1341 e 1342 del Codice Civile, dichiaro di aver letto e di approvare specificamente le clausole relative a: tempi di consegna, penali, limitazioni di responsabilità e Foro competente.</span>
            </label>
          )}
          {!hasVexatious && <div className="mb-2" />}
          {earlyActive && earlyVariants && (
            <label className="flex items-start gap-2.5 mb-4 cursor-pointer rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="public-early-consent-wrap">
              <Checkbox data-testid="public-accept-early-checkbox" checked={agreedEarly} onCheckedChange={setAgreedEarly} className="mt-0.5" />
              <span className="text-sm text-amber-800 leading-snug">
                Prendo atto e accetto che lo sconto è vincolato al pagamento entro il <strong>{quote.early_discount_deadline ? fmtDate(quote.early_discount_deadline) : "termine indicato"}</strong>; qualora il pagamento avvenga oltre tale data, mi impegno a corrispondere l'importo pieno di <strong>{euro(earlyVariants.full, cur)}</strong>.
              </span>
            </label>
          )}
          <Button
            data-testid="public-accept-submit-button"
            onClick={accept}
            disabled={submitting || !canAccept}
            className="w-full h-11 font-semibold"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accetta e Firma Preventivo"}
          </Button>
          <button
            data-testid="public-reject-open-button"
            onClick={() => setRejectOpen(true)}
            className="w-full mt-3 text-sm font-medium text-slate-500 hover:text-red-600 transition-colors"
          >
            Rifiuta il preventivo
          </button>
        </>
      )}
    </div>
  );
}
